/**
 * @module bridges
 * @package @geenius/adapters/react-daisyui
 * @description daisyUI class-based bridge components for async adapter
 * loading, error, and notification surfaces. Adapter contracts stay delegated
 * to the React reference variant.
 */

"use client";

import type { LoggerAdapter, LogMeta } from "@geenius/adapters";
import {
  Component,
  type ErrorInfo,
  type HTMLAttributes,
  type JSX,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";

type BridgeSize = "xs" | "sm" | "md" | "lg";

type ToastBridgeTone = "default" | "info" | "success" | "warning" | "error";

type ToastBridgePlacement = "start" | "center" | "end";

function cx(...classes: Array<string | false | null | undefined>): string {
  return classes.filter(Boolean).join(" ");
}

function normalizeProgress(progress: number | undefined): number | undefined {
  if (progress === undefined) {
    return undefined;
  }

  return Math.min(100, Math.max(0, progress));
}

function normalizeError(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }

  return new Error(typeof error === "string" ? error : "Adapter bridge error");
}

const loadingSizeClasses: Record<BridgeSize, string> = {
  xs: "loading-xs",
  sm: "loading-sm",
  md: "loading-md",
  lg: "loading-lg",
};

const alertToneClasses: Record<ToastBridgeTone, string> = {
  default: "",
  info: "alert-info",
  success: "alert-success",
  warning: "alert-warning",
  error: "alert-error",
};

const toastPlacementClasses: Record<ToastBridgePlacement, string> = {
  start: "toast-start",
  center: "toast-center",
  end: "toast-end",
};

function nodeText(value: ReactNode, fallback: string): string {
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }

  return fallback;
}

export interface LoadingBridgeProps extends HTMLAttributes<HTMLDivElement> {
  /** Controls whether the loading affordance renders. Defaults to true. */
  loading?: boolean;
  /** Alias for `loading` used by async adapter state objects. */
  isLoading?: boolean;
  /** Main loading label announced to assistive technology. */
  label?: ReactNode;
  /** Optional supporting copy rendered below the label. */
  description?: ReactNode;
  /** Optional determinate progress value from 0 through 100. */
  progress?: number;
  /** Visual density of the daisyUI loading spinner. */
  size?: BridgeSize;
  /** Content rendered when loading is explicitly false. */
  children?: ReactNode;
}

/** Renders a daisyUI loading affordance for async adapter work. */
export function LoadingBridge({
  children,
  className,
  description,
  isLoading,
  label = "Loading adapters",
  loading,
  progress,
  size = "md",
  ...props
}: LoadingBridgeProps): JSX.Element {
  const active = isLoading ?? loading ?? true;

  if (!active) {
    return <>{children}</>;
  }

  const normalizedProgress = normalizeProgress(progress);
  const progressLabel = nodeText(label, "Loading adapters");

  return (
    <div
      aria-busy="true"
      aria-live="polite"
      className={cx("alert items-start gap-3", className)}
      role="status"
      {...props}
    >
      <span
        aria-hidden="true"
        className={cx(
          "loading loading-spinner shrink-0",
          loadingSizeClasses[size],
        )}
      />
      <span className="grid min-w-0 gap-1">
        <span className="font-medium">{label}</span>
        {description ? (
          <span className="text-sm opacity-75">{description}</span>
        ) : null}
        {normalizedProgress !== undefined ? (
          <progress
            aria-label={progressLabel}
            className="progress progress-primary w-full"
            max={100}
            value={normalizedProgress}
          />
        ) : null}
      </span>
    </div>
  );
}

type ErrorBoundaryFallback =
  | ReactNode
  | ((error: Error, reset: () => void) => ReactNode);

export interface ErrorBoundaryBridgeProps {
  /** React subtree protected by the boundary. */
  children?: ReactNode;
  /** Optional controlled async adapter error. */
  error?: unknown;
  /** Optional custom fallback node or renderer. */
  fallback?: ErrorBoundaryFallback;
  /** Heading used by the default daisyUI fallback. */
  title?: ReactNode;
  /** Supporting copy used by the default fallback. */
  description?: ReactNode;
  /** Label for the default reset action. */
  resetLabel?: ReactNode;
  /** Additional class names for the default alert. */
  className?: string;
  /** Called when a descendant throws. */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /** Called before local error state is reset. */
  onReset?: () => void;
}

interface ErrorBoundaryBridgeState {
  caughtError: Error | null;
}

/** Catches React render failures and displays a daisyUI adapter error state. */
export class ErrorBoundaryBridge extends Component<
  ErrorBoundaryBridgeProps,
  ErrorBoundaryBridgeState
> {
  state: ErrorBoundaryBridgeState = { caughtError: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryBridgeState {
    return { caughtError: normalizeError(error) };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.props.onError?.(normalizeError(error), errorInfo);
  }

  private reset = (): void => {
    this.props.onReset?.();
    this.setState({ caughtError: null });
  };

  render(): ReactNode {
    const error =
      this.props.error !== undefined
        ? normalizeError(this.props.error)
        : this.state.caughtError;

    if (!error) {
      return this.props.children;
    }

    if (typeof this.props.fallback === "function") {
      return this.props.fallback(error, this.reset);
    }

    if (this.props.fallback) {
      return this.props.fallback;
    }

    return (
      <div
        className={cx("alert alert-error items-start", this.props.className)}
        role="alert"
      >
        <span aria-hidden="true">!</span>
        <div className="min-w-0">
          <div className="font-semibold">
            {this.props.title ?? "Adapter error"}
          </div>
          {this.props.description ? (
            <div className="text-sm opacity-85">{this.props.description}</div>
          ) : null}
          <div className="break-words text-sm opacity-85">{error.message}</div>
          <button
            className="btn btn-error btn-sm mt-3"
            onClick={this.reset}
            type="button"
          >
            {this.props.resetLabel ?? "Retry"}
          </button>
        </div>
      </div>
    );
  }
}

export interface ToastBridgeProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  /** Controls whether the toast is currently visible. */
  open?: boolean;
  /** Visual treatment matching daisyUI alert tones. */
  variant?: ToastBridgeTone;
  /** Alias for `variant` for libraries that call this value status. */
  status?: ToastBridgeTone;
  /** Toast title. */
  title?: ReactNode;
  /** Toast message, rendered after the title. */
  message?: ReactNode;
  /** Optional supporting message. */
  description?: ReactNode;
  /** Optional action label rendered as a daisyUI button. */
  actionLabel?: ReactNode;
  /** Called when the optional action is pressed. */
  onAction?: () => void;
  /** Called when the dismiss button is pressed or auto-dismiss fires. */
  onDismiss?: () => void;
  /** Auto-dismiss timeout in milliseconds; 0 disables auto-dismiss. */
  duration?: number;
  /** Toast placement on the horizontal axis. */
  placement?: ToastBridgePlacement;
  /** Optional logger adapter that receives one structured event per visible toast. */
  logger?: LoggerAdapter;
  /** Optional structured metadata merged into the logger event. */
  meta?: LogMeta;
  /** Optional subtree rendered next to the declarative toast bridge. */
  children?: ReactNode;
}

/** Renders daisyUI toast notifications for adapter telemetry. */
export function ToastBridge({
  actionLabel,
  children,
  className,
  description,
  duration = 0,
  logger,
  message,
  meta,
  onAction,
  onDismiss,
  open = true,
  placement = "end",
  status,
  title,
  variant,
  ...props
}: ToastBridgeProps): JSX.Element | null {
  const [visible, setVisible] = useState(open);
  const loggedToastKey = useRef<string | null>(null);
  const tone = variant ?? status ?? "info";

  useEffect(() => {
    setVisible(open);
  }, [open]);

  useEffect(() => {
    if (!visible) {
      loggedToastKey.current = null;
      return;
    }

    if (!logger) {
      return;
    }

    const titleText = nodeText(title ?? message, "Adapter notification");
    const descriptionText =
      typeof description === "string" ? description : undefined;
    const toastKey = `${tone}:${titleText}:${descriptionText ?? ""}`;

    if (loggedToastKey.current === toastKey) {
      return;
    }

    loggedToastKey.current = toastKey;
    const payload: LogMeta = {
      bridge: "react-daisyui",
      variant: tone,
      ...meta,
    };

    if (descriptionText) {
      payload.description = descriptionText;
    }

    if (tone === "error") {
      void logger.error(titleText, payload);
      return;
    }

    if (tone === "warning") {
      void logger.warn(titleText, payload);
      return;
    }

    void logger.info(titleText, payload);
  }, [description, logger, message, meta, title, tone, visible]);

  useEffect(() => {
    if (!visible || duration <= 0) {
      return;
    }

    const timeout = globalThis.setTimeout(() => {
      setVisible(false);
      onDismiss?.();
    }, duration);

    return () => globalThis.clearTimeout(timeout);
  }, [duration, onDismiss, visible]);

  const dismiss = (): void => {
    setVisible(false);
    onDismiss?.();
  };

  const toast = visible ? (
    <div
      className={cx("toast", toastPlacementClasses[placement], className)}
      role={tone === "error" || tone === "warning" ? "alert" : "status"}
      {...props}
    >
      <div
        className={cx("alert items-start shadow-lg", alertToneClasses[tone])}
      >
        <div className="min-w-0">
          {title ? <div className="font-semibold">{title}</div> : null}
          {message ? <div className="text-sm">{message}</div> : null}
          {description ? (
            <div className="text-sm opacity-75">{description}</div>
          ) : null}
          {actionLabel ? (
            <button
              className="btn btn-ghost btn-xs mt-2"
              onClick={onAction}
              type="button"
            >
              {actionLabel}
            </button>
          ) : null}
        </div>
        <button
          aria-label="Dismiss notification"
          className="btn btn-ghost btn-xs"
          onClick={dismiss}
          type="button"
        >
          x
        </button>
      </div>
    </div>
  ) : null;

  if (!children) {
    return toast;
  }

  return (
    <>
      {children}
      {toast}
    </>
  );
}
